/**
 * `@dxf-ai-editor/acadctb` — ASCII tag-tree lexer/parser.
 *
 * The acadctb body grammar is a hierarchical tag tree:
 *
 *   ```
 *   key=value      (leaf)
 *   key{           (block open)
 *       inner_key=value
 *       inner_block{ ... }
 *   }              (block close)
 *   ```
 *
 * Lines are CRLF or LF terminated; values do not use quoting. Keys
 * terminate at whitespace / `=` / `{` / `}` / EOL. Block boundaries
 * may appear on the same physical line (`name=Color_1\r\n}` is legal).
 *
 * The lexer produces an {@link AsciiNode} tree which the higher-level
 * builders (`./entries.ts`) consume. Structural failures throw a
 * `ParseAcadctbError` with `code = "grammar"`.
 */

import { makeError } from "./errors";

export type AsciiNode =
  | { kind: "leaf"; key: string; value: string }
  | { kind: "block"; key: string; children: AsciiNode[] };

interface LexerState {
  text: string;
  pos: number;
  line: number;
}

export function parseTagTree(body: string): AsciiNode[] {
  const state: LexerState = { text: body, pos: 0, line: 1 };
  const root: AsciiNode[] = [];
  parseSiblings(state, root, /* atTopLevel */ true);
  return root;
}

function parseSiblings(
  state: LexerState,
  out: AsciiNode[],
  atTopLevel: boolean,
): void {
  while (state.pos < state.text.length) {
    skipWhitespace(state);
    if (state.pos >= state.text.length) {
      return;
    }
    const ch = state.text.charAt(state.pos);
    if (ch === "}") {
      if (atTopLevel) {
        throw makeError(
          "grammar",
          `unexpected '}' at top level (line ${state.line})`,
        );
      }
      state.pos += 1;
      return;
    }
    const key = readKey(state);
    if (key.length === 0) {
      throw makeError(
        "grammar",
        `empty key at line ${state.line} (pos ${state.pos})`,
      );
    }
    skipInlineWhitespace(state);
    if (state.pos >= state.text.length) {
      throw makeError(
        "grammar",
        `key '${key}' has no '{' or '=' (line ${state.line})`,
      );
    }
    const sep = state.text.charAt(state.pos);
    if (sep === "{") {
      state.pos += 1;
      const children: AsciiNode[] = [];
      parseSiblings(state, children, /* atTopLevel */ false);
      out.push({ kind: "block", key, children });
    } else if (sep === "=") {
      state.pos += 1;
      const value = readValue(state);
      out.push({ kind: "leaf", key, value });
    } else {
      throw makeError(
        "grammar",
        `unexpected separator '${sep}' after key '${key}' (line ${state.line})`,
      );
    }
  }
  if (!atTopLevel) {
    throw makeError("grammar", `unterminated block (expected '}')`);
  }
}

function skipWhitespace(state: LexerState): void {
  while (state.pos < state.text.length) {
    const ch = state.text.charCodeAt(state.pos);
    if (ch === 0x20 || ch === 0x09 || ch === 0x0d) {
      state.pos += 1;
    } else if (ch === 0x0a) {
      state.line += 1;
      state.pos += 1;
    } else {
      return;
    }
  }
}

function skipInlineWhitespace(state: LexerState): void {
  while (state.pos < state.text.length) {
    const ch = state.text.charCodeAt(state.pos);
    if (ch === 0x20 || ch === 0x09) {
      state.pos += 1;
    } else {
      return;
    }
  }
}

function readKey(state: LexerState): string {
  const start = state.pos;
  while (state.pos < state.text.length) {
    const ch = state.text.charCodeAt(state.pos);
    // Key terminates at whitespace, '=', '{', '}', '\n', '\r'.
    if (
      ch === 0x20 ||
      ch === 0x09 ||
      ch === 0x3d /* = */ ||
      ch === 0x7b /* { */ ||
      ch === 0x7d /* } */ ||
      ch === 0x0a ||
      ch === 0x0d
    ) {
      break;
    }
    state.pos += 1;
  }
  return state.text.slice(start, state.pos);
}

function readValue(state: LexerState): string {
  // Skip any leading inline whitespace after '='.
  skipInlineWhitespace(state);
  // Value terminates at end-of-line. Quoted strings are not used in
  // the acadctb format (AutoCAD writes raw values directly).
  const start = state.pos;
  while (state.pos < state.text.length) {
    const ch = state.text.charCodeAt(state.pos);
    if (ch === 0x0a || ch === 0x0d) {
      break;
    }
    state.pos += 1;
  }
  return state.text.slice(start, state.pos).trimEnd();
}

/* --------------------------------------------------------------- *\
 * Tag-tree lookup helpers — shared by `./entries.ts` consumers.
\* --------------------------------------------------------------- */

export function findLeaf(
  siblings: AsciiNode[],
  key: string,
): string | undefined {
  for (const node of siblings) {
    if (node.kind === "leaf" && node.key === key) {
      return node.value;
    }
  }
  return undefined;
}

export function findBlock(
  siblings: AsciiNode[],
  key: string,
): AsciiNode[] | undefined {
  for (const node of siblings) {
    if (node.kind === "block" && node.key === key) {
      return node.children;
    }
  }
  return undefined;
}
